import debounce from "lodash.debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppState, type AppStateStatus, Platform } from "react-native";
import {
  clearRecentTasks,
  loadAllTasks,
  loadLastDateTasks,
  loadSaveForLaterTasks,
  loadSearchResultTasks,
  moveTasksToJustToday,
  removeDateFromSFLTask,
  resetSearchResultTask,
} from "../../services/tasksService";
import {
  getUserMetaData,
  setDailyImportDate,
} from "../../services/userService";
import type { MappedTasks } from "../../types/Task";
import { parseCurrentDateTimeIso } from "../../utils/date";
import { delay } from "../../utils/delay";
import { warningToast } from "../../utils/toast";

interface UseOnEnterProps {
  allTasks: { [key: string]: MappedTasks };
  route: any;
  navigation: any;
}

interface UseOnEnterReturn {
  yesterdayToTodayTasksIds: string[];
  setYesterdayToTodayTaskIds: (ids: string[]) => void;
  saveForLaterToTodayTaskIdsRecent: string[];
  setSaveForLaterToTodayTaskIdsRecent: (ids: string[]) => void;
  saveForLaterToTodayTaskIds: string[];
  setSaveForLaterToTodayTaskIds: (ids: string[]) => void;
  filterIndex: string;
  setFilterIndex: (index: string) => void;
  clicked: boolean;
  setClicked: (clicked: boolean) => void;
  isSearchResults: boolean;
  setIsSearchResults: (isSearchResults: boolean) => void;
  searchPhrase: string;
  setSearchPhrase: (phrase: string) => void;
  shouldUseAllOnNextSearch: boolean;
  setShouldUseAllOnNextSearch: (shouldUse: boolean) => void;
  yesterdayIncompleteTasks: string[];
  showPage1: boolean;
  showPage2: boolean;
  moveTasksToToday: () => Promise<void>;
  done: () => void;
}

export const useOnEnter = ({
  allTasks,
  route,
  navigation,
}: UseOnEnterProps): UseOnEnterReturn => {
  const [yesterdayToTodayTasksIds, setYesterdayToTodayTaskIds] = useState<
    string[]
  >([]);
  const [
    saveForLaterToTodayTaskIdsRecent,
    setSaveForLaterToTodayTaskIdsRecent,
  ] = useState<string[]>([]);
  const [saveForLaterToTodayTaskIds, setSaveForLaterToTodayTaskIds] = useState<
    string[]
  >([]);
  const [filterIndex, setFilterIndex] = useState<string>("contextless");
  const [clicked, setClicked] = useState(false);
  const [isSearchResults, setIsSearchResults] = useState(false);
  const [searchPhrase, setSearchPhrase] = useState<string>("");
  const [shouldUseAllOnNextSearch, setShouldUseAllOnNextSearch] =
    useState<boolean>(true);

  const appState = useRef(AppState.currentState);
  const [appStateVisible, setAppStateVisible] = useState(appState.current);

  const yesterdayIncompleteTasks = Object.keys(
    allTasks["JustYesterdayIncomplete"],
  ).filter((taskid) => !allTasks["JustYesterdayIncomplete"][taskid].checked);

  const recentTasks =
    route.params && route.params.recentTasks ? route.params.recentTasks : null;

  const showPage1 = yesterdayIncompleteTasks.length > 0;
  const showPage2 = Array.isArray(recentTasks) && recentTasks.length > 0;

  const seperateYesterdayTaskIds = () => {
    const moveToJustTodayTaskIds: string[] = [];
    const moveToSaveForLaterTaskIds: string[] = [];

    for (const taskid of yesterdayIncompleteTasks) {
      if (yesterdayToTodayTasksIds.includes(taskid)) {
        moveToJustTodayTaskIds.push(taskid);
      } else {
        moveToSaveForLaterTaskIds.push(taskid);
      }
    }
    return [moveToJustTodayTaskIds, moveToSaveForLaterTaskIds];
  };

  const moveTasksToToday = async () => {
    const [toToday, toSaveForlater] = seperateYesterdayTaskIds();

    const mergedTaskIdsToJustToday = Array.from(
      new Set([
        ...toToday,
        ...saveForLaterToTodayTaskIdsRecent,
        ...saveForLaterToTodayTaskIds,
      ]),
    );

    if (mergedTaskIdsToJustToday.length > 0) {
      await moveTasksToJustToday(mergedTaskIdsToJustToday);
    }

    if (toSaveForlater.length > 0) {
      await removeDateFromSFLTask(toSaveForlater);
    }
  };

  const done = () => {
    if (Platform.OS !== "web") warningToast("Moving...", "bottom");

    moveTasksToToday()
      .then(() => {
        clearRecentTasks()
          .then(() => {
            setDailyImportDate()
              .then(() => {
                loadAllTasks()
                  .then(() => {
                    navigation.goBack();
                  })
                  .catch((e) => {
                    console.log("");
                  });
              })
              .catch((e) => {});
          })
          .catch((e) => {
            console.log("");
          });
      })
      .catch((e) => {
        console.log("");
      });
  };

  const handleSearchPhraseChange = (searchPhraseVal: string) => {
    if (searchPhraseVal) {
      loadSearchResultTasks({
        search_term: searchPhraseVal,
        context_id: filterIndex,
        task_type: "SAVE_FOR_LATER",
      });
    }
  };

  const debouncedHandleSearchPhraseChange = useMemo(
    () => debounce(handleSearchPhraseChange, 300),
    [filterIndex],
  );

  useEffect(() => {
    const onReturnEvent = () => {
      getUserMetaData()
        .then((resp) => {
          const nowDate = parseCurrentDateTimeIso();
          if (resp) {
            if (
              resp.last_daily_import_date &&
              resp.last_daily_import_date === nowDate
            ) {
              delay(15).then(() => {
                navigation.goBack();
              });
            }
          }
        })
        .catch((e) => {});
    };

    const eventHandler = (nextAppState: AppStateStatus) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === "active"
      ) {
        onReturnEvent();
      }
      appState.current = nextAppState;
      setAppStateVisible(appState.current);
    };

    loadLastDateTasks().then(() => {
      if (isSearchResults) {
        loadSearchResultTasks({
          search_term: searchPhrase,
          context_id: filterIndex,
          task_type: "SAVE_FOR_LATER",
        });
      } else {
        loadSaveForLaterTasks();
      }
    });

    const subscription = AppState.addEventListener("change", eventHandler);

    return () => {
      subscription.remove();
    };
  }, [
    isSearchResults,
    filterIndex,
    showPage1,
    showPage2,
    navigation,
    searchPhrase,
  ]);

  return {
    yesterdayToTodayTasksIds,
    setYesterdayToTodayTaskIds,
    saveForLaterToTodayTaskIdsRecent,
    setSaveForLaterToTodayTaskIdsRecent,
    saveForLaterToTodayTaskIds,
    setSaveForLaterToTodayTaskIds,
    filterIndex,
    setFilterIndex,
    clicked,
    setClicked,
    isSearchResults,
    setIsSearchResults,
    searchPhrase,
    setSearchPhrase,
    shouldUseAllOnNextSearch,
    setShouldUseAllOnNextSearch,
    yesterdayIncompleteTasks,
    showPage1,
    showPage2,
    moveTasksToToday,
    done,
  };
};
